const PHONE_NUMBER_FORMAT_REGEX = /^(\d{0,3})(\d{0,3})(\d{0,2})(\d{0,2})$/;
const PHONE_NUMBER_UNFORMATTED_REGEX = /(\s|\(|\)|-)/g;
const MAX_RU_PHONE_WITH_PREFIX_DIGITS = 11;
const MAX_RU_PHONE_NATIONAL_DIGITS = 10;
const MAX_PHONE_NATIONAL_DIGITS = 20;

const clampRuPhoneDigits = (cleaned: string) =>
    cleaned.startsWith('7') || cleaned.startsWith('8')
        ? cleaned.slice(0, MAX_RU_PHONE_WITH_PREFIX_DIGITS)
        : cleaned.slice(0, MAX_RU_PHONE_NATIONAL_DIGITS);

const formatRuNationalBody = (digits: string) => {
    const match = digits.match(PHONE_NUMBER_FORMAT_REGEX)!;

    return [match[1] ? `(${match[1]}` : '', match[2] ? `) ${match[2]}` : '', match[3] ? ` ${match[3]}` : '', match[4] ? `-${match[4]}` : ''].join('');
};

export const formatPhoneNumberInput = ({ phoneNumber }: { phoneNumber: string }) => {
    const trimmed = phoneNumber.trimStart();
    const rawHasPlusPrefix = trimmed.startsWith('+');
    const cleaned = clampRuPhoneDigits(phoneNumber.replace(/\D/g, ''));

    if (!cleaned) {
        return '';
    }

    if (rawHasPlusPrefix && cleaned.startsWith('7') && cleaned.length < MAX_RU_PHONE_WITH_PREFIX_DIGITS) {
        const body = cleaned.slice(1);

        return body ? `+7 ${formatRuNationalBody(body)}` : '+7';
    }

    if (cleaned.length === 11 && (cleaned.startsWith('7') || cleaned.startsWith('8'))) {
        const nationalPrefix = cleaned[0] === '8' ? '8' : '+7';
        const body = cleaned.slice(1);

        return [nationalPrefix, `(${body.slice(0, 3)})`, `${body.slice(3, 6)}-${body.slice(6, 8)}-${body.slice(8, 10)}`].filter(Boolean).join(' ');
    }

    return formatRuNationalBody(cleaned);
};

export const unformatPhoneNumberInput = ({ phoneNumber }: { phoneNumber: string }) => phoneNumber.replace(PHONE_NUMBER_UNFORMATTED_REGEX, '');

const normalizeCallingCodeDigits = (countryCode: string) => countryCode.replace(/\D/g, '');

const normalizeSelectedCountryNationalDigits = ({ phoneNumber, countryCode }: { phoneNumber: string; countryCode: string }) => {
    const rawValue = phoneNumber.trim();
    const cleaned = unformatPhoneNumberInput({ phoneNumber }).replace(/\D/g, '');
    const countryCodeDigits = normalizeCallingCodeDigits(countryCode);
    const hasExplicitCallingCode = rawValue.startsWith('+') && countryCodeDigits && cleaned.startsWith(countryCodeDigits);
    const nationalDigits = hasExplicitCallingCode ? cleaned.slice(countryCodeDigits.length) : cleaned;

    return nationalDigits.slice(0, MAX_PHONE_NATIONAL_DIGITS);
};

export const formatAuthPhoneNumberInput = ({
    phoneNumber,
    countryOptionId,
    countryCode,
}: {
    phoneNumber: string;
    countryOptionId: string;
    countryCode: string;
}) => {
    if (countryOptionId === 'RU') {
        return formatPhoneNumberInput({ phoneNumber });
    }

    return normalizeSelectedCountryNationalDigits({
        phoneNumber,
        countryCode,
    });
};

export const normalizePhoneNumberForBackend = ({
    phoneNumber,
    countryOptionId = 'RU',
    countryCode = '+7',
}: {
    phoneNumber: string;
    countryOptionId?: string;
    countryCode?: string;
}) => {
    if (countryOptionId !== 'RU') {
        return normalizeSelectedCountryNationalDigits({
            phoneNumber,
            countryCode,
        });
    }

    const cleaned = clampRuPhoneDigits(unformatPhoneNumberInput({ phoneNumber }).replace(/\D/g, ''));

    if (cleaned.length === 11 && (cleaned.startsWith('7') || cleaned.startsWith('8'))) {
        return cleaned.slice(1);
    }

    return cleaned;
};

export const buildPhoneE164 = ({
    phoneNumber,
    countryCode,
    countryOptionId,
}: {
    phoneNumber: string;
    countryCode: string;
    countryOptionId: string;
}) => {
    const nationalDigits = normalizePhoneNumberForBackend({
        phoneNumber,
        countryCode,
        countryOptionId,
    });

    return `${countryCode}${nationalDigits}`;
};

export const formatPhoneE164ForDisplay = ({ phoneE164 }: { phoneE164: string | null | undefined }) => {
    const value = String(phoneE164 ?? '').trim();

    if (!/^\+[1-9]\d{1,14}$/.test(value)) {
        return null;
    }

    const digits = value.slice(1);

    if (value.startsWith('+7') && digits.length === 11) {
        const body = digits.slice(1);

        return `+7 (${body.slice(0, 3)}) ${body.slice(3, 6)}-${body.slice(6, 8)}-${body.slice(8, 10)}`;
    }

    return value;
};
